import noop from 'lodash/noop';
import { useEffect, useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Separator,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { EluveEditor, EluveEditorHandle } from '@eluve/eluve-editor';
import { useAnalytics } from '@eluve/posthog-react';

import { ChatMarkdown } from './ChatMarkdown';
import { ToolInvocation } from './ToolCallStatus';

interface DocumentPreviewCardProps {
  toolInvocation: ToolInvocation;
  onDiscard: (message: string) => void;
  /** Web variant only; absence flips header to [Copy][Discard] and hides the bottom Copy row. */
  onCommit?: (
    draft: { title: string; content: string; docType?: string },
    edited: boolean,
  ) => Promise<void>;
}

type CardState = 'idle' | 'added' | 'discarded';

interface DocumentDraft {
  title: string;
  content: string;
  docType?: string;
}

function parseDraft(toolInvocation: ToolInvocation): DocumentDraft | null {
  const data =
    toolInvocation.state === 'output-available'
      ? toolInvocation.output
      : toolInvocation.input;

  if (!data || typeof data !== 'object') return null;

  const { title, content, docType } = data as Record<string, unknown>;
  if (typeof title !== 'string' || typeof content !== 'string') {
    return null;
  }

  return {
    title,
    content,
    docType: typeof docType === 'string' ? docType : undefined,
  };
}

interface DocumentCardContentProps {
  originalDraft: DocumentDraft;
  onDiscard: (message: string) => void;
  onCommit?: (
    draft: { title: string; content: string; docType?: string },
    edited: boolean,
  ) => Promise<void>;
}

const DocumentCardContent = ({
  originalDraft,
  onCommit,
  onDiscard,
}: DocumentCardContentProps) => {
  const toast = useToast();
  const { capture } = useAnalytics();
  const canCommit = onCommit !== undefined;
  const [cardState, setCardState] = useState<CardState>('idle');
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(originalDraft.title);
  // Snapshot of the final content captured at add-time for the post-save
  // read-only ChatMarkdown render. Avoids a per-keystroke getMarkdown() + setState.
  const [savedContent, setSavedContent] = useState(originalDraft.content);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const editorRef = useRef<EluveEditorHandle | null>(null);

  useEffect(() => {
    return () => clearTimeout(copyTimerRef.current);
  }, []);

  const isActed = cardState !== 'idle';

  const readCurrentContent = () =>
    editorRef.current?.getMarkdown() ?? originalDraft.content;

  const handleCopy = async () => {
    try {
      const content = isActed ? savedContent : readCurrentContent();
      await navigator.clipboard.writeText(content);
      setCopied(true);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may fail in some contexts
    }
  };

  const handleAdd = async () => {
    const content = readCurrentContent();
    const trimmedTitle = currentTitle.trim() || originalDraft.title;
    const isEdited =
      trimmedTitle !== originalDraft.title || content !== originalDraft.content;

    setIsSaving(true);
    try {
      await onCommit?.(
        {
          title: trimmedTitle,
          content,
          docType: originalDraft.docType,
        },
        isEdited,
      );
      setSavedContent(content);
      setCurrentTitle(trimmedTitle);
      setCardState('added');
    } catch {
      toast.error('Failed to save document. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardConfirm = () => {
    capture('document_discarded', {
      saved: false,
      origin: 'chat',
    });
    setShowDiscardDialog(false);
    setCardState('discarded');
    onDiscard("Discard the document, I don't need it.");
  };

  return (
    <>
      <Stack
        className={`w-full rounded-lg border border-border-primary p-4 transition-opacity ${
          isActed ? 'opacity-60' : ''
        }`}
        gap={2}
      >
        <Stack justify="between" gap={2} direction="horizontal">
          {isActed || !canCommit ? (
            <Text size="xs" weight="semibold">
              {currentTitle}
            </Text>
          ) : (
            <Input
              value={currentTitle}
              onChange={(e) => setCurrentTitle(e.target.value)}
              placeholder="Document title"
              aria-label="Document title"
              disabled={isSaving}
              className="-mx-1 h-auto rounded-md border-none bg-transparent px-1 py-0.5 text-sm font-semibold shadow-none transition-colors hover:bg-bg-secondary focus-visible:bg-bg-secondary focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-hidden"
            />
          )}
          {cardState === 'idle' && (
            <Stack fullWidth={false} gap={3} direction="horizontal">
              {canCommit ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAdd}
                  disabled={isSaving}
                >
                  <Icon
                    name="Check"
                    size="auto"
                    className="text-text-positive"
                  />
                  Add Document
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  <Icon name={copied ? 'Check' : 'Copy'} size="auto" />
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDiscardDialog(true)}
                disabled={isSaving}
              >
                <Icon name="X" size="auto" className="text-text-negative" />
                Discard
              </Button>
            </Stack>
          )}
          {cardState === 'added' && (
            <Stack fullWidth={false} gap={1} direction="horizontal">
              <Icon name="CheckCircle2" className="text-text-positive" />
              <Text size="xs" color="secondary">
                Added to notes
              </Text>
            </Stack>
          )}
          {cardState === 'discarded' && (
            <Text size="xs" color="tertiary">
              Discarded
            </Text>
          )}
        </Stack>

        <Separator className="my-3" />

        <Stack className="max-h-[500px] overflow-y-auto" gap={2}>
          {isActed ? (
            <ChatMarkdown>{savedContent}</ChatMarkdown>
          ) : (
            <EluveEditor
              ref={editorRef}
              content={originalDraft.content}
              onUpdate={noop}
            />
          )}
        </Stack>

        {canCommit && (
          <>
            <Separator className="my-3" />

            <Stack gap={2} className="self-start" direction="horizontal">
              <Button variant="ghost" size="sm" onClick={handleCopy}>
                <Icon name={copied ? 'Check' : 'Copy'} size="auto" />
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Stack>
          </>
        )}
      </Stack>

      <Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard document?</DialogTitle>
            <DialogDescription>
              This will discard the drafted document. You can always ask the
              chat to generate a new one.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDiscardDialog(false)}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleDiscardConfirm}>
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const DocumentPreviewCard = ({
  toolInvocation,
  onCommit,
  onDiscard,
}: DocumentPreviewCardProps) => {
  const isComplete = toolInvocation.state === 'output-available';

  if (!isComplete) {
    return (
      <Stack
        inline
        gap={2}
        className="rounded-full border border-border-insights-subtle bg-bg-insights-subtle px-3 py-1.5"
        direction="horizontal"
      >
        <Icon name="FileText" className="animate-pulse text-text-insights" />
        <Text size="xs" weight="medium" color="primary">
          Drafting document…
        </Text>
      </Stack>
    );
  }

  const originalDraft = parseDraft(toolInvocation);
  if (!originalDraft) {
    return null;
  }

  return (
    <DocumentCardContent
      originalDraft={originalDraft}
      onCommit={onCommit}
      onDiscard={onDiscard}
    />
  );
};
